import type { AppAbility } from '@/lib/casl'
import type { QueryClient } from '@tanstack/react-query'
import { SalesReportError } from '@/components/money/SalesReportError'
import { LoadingState } from '@/components/patterns/loading-state'
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
  useNavigate,
  type SearchSchemaInput,
  type ResolveParams,
  type FileRoutesByPath,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, BookOpen, Wallet } from 'lucide-react'
import { z } from 'zod/v4'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { MoneySummary } from '@/components/money/MoneySummary'
import {
  SalesBreakdown,
  getSalesSummaryMetrics,
  getSalesComponents,
  getPaidOrOwedLabels,
} from '@/components/money/SalesOverview'
import { SalesReportActions } from '@/components/money/SalesReportActions'
import { SalesCashActivity } from '@/components/money/SalesDailyTable'
import { SalesDayLedger } from '@/components/money/SalesDayLedger'
import { usePathHelper } from '@/hooks/usePathHelper'
import { formatCalendarDate, getErrorMessage } from '@/lib/utils'
import { adminTitleHead } from '@/lib/admin-route-title'
import { canDisplaySalesReport, moneyQueries } from '@/lib/services/money/money.query'
import { bookedSalesFilters, bookedSalesDaySearchSchema } from '@/lib/services/money/money.schema'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'

export async function loadSalesDayRoute({
  context,
  params,
  deps,
}: {
  context: {
    ability: Pick<AppAbility, 'can'>
    queryClient: Pick<QueryClient, 'ensureQueryData' | 'prefetchQuery'>
  }
  params: { orgId: number; date: string }
  deps: { search: z.output<typeof bookedSalesDaySearchSchema> }
}) {
  if (!context.ability.can('list', 'transactions')) throw notFound()
  if (+params.orgId === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID))
    throw redirect({
      to: '/$orgId/reports',
      params: { orgId: params.orgId },
      search: {},
      replace: true,
    })
  if (!z.iso.date().safeParse(params.date).success) throw notFound()
  const organization = await context.queryClient.ensureQueryData(
    organizationQueries.show(params.orgId)
  )
  const range = bookedSalesFilters(deps.search, organization.timezone)
  const filters = {
    start_date: params.date,
    end_date: params.date,
    page: deps.search.page ?? (deps.search.highlight_order_id ? undefined : 1),
    ...(deps.search.highlight_order_id && { highlight_order_id: deps.search.highlight_order_id }),
  }
  await context.queryClient.prefetchQuery(
    moneyQueries.sales({ organizationId: params.orgId, filters })
  )
  return { filters, range: { start_date: range.start_date, end_date: range.end_date } }
}

// The router reads its phantom input marker from the last signature; raw URL inputs use the same Zod owner.
type SalesDaySearchValidator = {
  (search: z.input<typeof bookedSalesDaySearchSchema>): z.output<typeof bookedSalesDaySearchSchema>
  (
    search: z.input<typeof bookedSalesDaySearchSchema> & SearchSchemaInput
  ): z.output<typeof bookedSalesDaySearchSchema>
}
export const validateSalesDaySearch: SalesDaySearchValidator = (
  search: z.input<typeof bookedSalesDaySearchSchema>
) => bookedSalesDaySearchSchema.parse(search)

export const Route = createFileRoute('/_authenticated/$orgId/reports/sales-summary/$date')({
  component: SalesDayPage,
  errorComponent: ({ error }) => (
    <InfoNotice tone="warning" title="Sales unavailable">
      {getErrorMessage(error)}
    </InfoNotice>
  ),
  head: ({
    params,
  }: {
    params: Pick<
      ResolveParams<FileRoutesByPath['/_authenticated/$orgId/reports/sales-summary/$date']['path']>,
      'date'
    >
  }) => adminTitleHead(formatCalendarDate(params.date), 'Sales Summary', 'Reports'),
  validateSearch: validateSalesDaySearch,
  loaderDeps: ({ search }): { search: z.output<typeof bookedSalesDaySearchSchema> } => ({ search }),
  loader: loadSalesDayRoute,
})

function SalesDayPage() {
  const { orgId, date } = Route.useParams()
  const { filters, range } = Route.useLoaderData()
  const {
    data: report,
    error,
    isError,
    isPending,
    isFetching,
    refetch,
  } = useQuery({
    ...moneyQueries.sales({ organizationId: orgId, filters }),
    // The loader already attempted this request; mounting the error UI must not repeat it.
    retryOnMount: false,
  })
  const showReport = canDisplaySalesReport({ data: report, isError, error })
  const path = usePathHelper()
  const navigate = useNavigate()
  return (
    <div className="@container/money space-y-6 pb-6">
      <Button asChild variant="link">
        <Link {...path.salesSummary.index().using({ orgId }).link} search={range}>
          <ArrowLeft />
          Back to Sales Summary · {formatCalendarDate(range.start_date)} –{' '}
          {formatCalendarDate(range.end_date)}
        </Link>
      </Button>
      <AdminPageHeader
        icon={BookOpen}
        title={`Sales for ${formatCalendarDate(date)}`}
        description="Orders placed, payments received, refunds, and cancellations for this day."
        actions={
          showReport && (
            <SalesReportActions
              organizationId={orgId}
              filters={{ start_date: date, end_date: date }}
            />
          )
        }
      />
      {isError && (
        <SalesReportError
          error={error}
          orgId={orgId}
          onRetry={() => refetch()}
          isRetrying={isFetching}
          hasCachedReport={showReport}
        />
      )}
      {isPending ? (
        <LoadingState label="Loading sales…" />
      ) : showReport && report ? (
        <>
          <MoneySummary
            title="This day’s activity"
            metrics={getSalesSummaryMetrics(report.summary)}
          />
          <SalesDayLedger
            report={report}
            orgId={orgId}
            page={report.filters.page ?? filters.page ?? 1}
            highlightedOrderId={filters.highlight_order_id}
            onPageChange={(page) =>
              navigate({
                ...path.salesSummary.show(date).using({ orgId }).link,
                search: { ...range, page, highlight_order_id: filters.highlight_order_id },
              })
            }
          />
          <div className="grid gap-5 @4xl/money:grid-cols-2">
            <SalesBreakdown
              title="Sales breakdown"
              subtitle="Products, discounts, tax, tips, and charges"
              values={report.summary.net_components}
              labels={getSalesComponents(report)}
              tone="success"
            />
            <SalesBreakdown
              title="Sales by payment method"
              subtitle="How customers paid, including house account charges"
              values={report.summary.payment_split}
              labels={getPaidOrOwedLabels(report)}
              icon={Wallet}
            />
          </div>
          <SalesCashActivity report={report} showReturns />
        </>
      ) : null}
    </div>
  )
}
